// dependencies
import React, { useEffect } from "react";

// utils
import { cn } from "../../../lib/utils";

// components
import { Text, TextProps } from "../Text";

export type CyclingTextProps = Omit<TextProps, "children" | "size" | "weight"> & {
	/**
	 * Array of strings to cycle through
	 */
	textArray: string[];
	/**
	 * Duration in milliseconds between text changes
	 * @default 2000
	 */
	switchTimer?: number;
	/**
	 * Text size
	 * @default "paragraph-3"
	 */
	size?: TextProps["size"];
	/**
	 * Font weight
	 * @default "regular"
	 */
	weight?: TextProps["weight"];
	/**
	 * Additional className for the container
	 */
	dangerouslySetClassName?: string;
};

/**
 * CyclingText component that cycles through an array of text strings
 * with minimal re-renders by using refs to directly manipulate the DOM
 */
export const CyclingText = React.forwardRef<HTMLSpanElement, CyclingTextProps>(function CyclingText(props, ref) {
	const {
		textArray,
		switchTimer = 2000,
		size = "paragraph-3",
		weight = "regular",
		dangerouslySetClassName,
		...textProps
	} = props;

	const spanRef = React.useRef<HTMLSpanElement>(null);
	const currentIndexRef = React.useRef(0);
	const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

	// Extract textArray stringification for dependency tracking
	const textArrayString = JSON.stringify(textArray);

	// Single effect to handle both initialization and cycling
	useEffect(() => {
		// Clear any existing interval
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		if (!textArray.length) return;

		// Reset to index 0 and set initial text
		currentIndexRef.current = 0;
		if (spanRef.current) {
			spanRef.current.textContent = textArray[0];
		}

		// Only set up interval if there's more than one text item
		if (textArray.length > 1) {
			intervalRef.current = setInterval(() => {
				currentIndexRef.current = (currentIndexRef.current + 1) % textArray.length;

				// Directly update DOM to avoid re-renders
				if (spanRef.current) {
					spanRef.current.textContent = textArray[currentIndexRef.current];
				}
			}, switchTimer);
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [textArray, textArrayString, switchTimer]);

	return (
		<Text
			{...textProps}
			size={size}
			weight={weight}
			ref={ref}
			dangerouslySetClassName={cn(dangerouslySetClassName)}
		>
			<span ref={spanRef} />
		</Text>
	);
});

CyclingText.displayName = "CyclingText";
